import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from 'react-oidc-context';

import { MAX_COMPARE_DATASETS } from './constants';
import { isSameDatasetRef } from './datasetRef';
import { CompareDataset, DatasetRef } from './types';

type CompareContextType = {
  datasets: CompareDataset[];
  addDataset: (dataset: CompareDataset) => void;
  removeDataset: (ref: DatasetRef) => void;
  clearDatasets: () => void;
  toggleDataset: (dataset: CompareDataset) => void;
  isSelected: (ref: DatasetRef) => boolean;
  isFull: boolean;
  canCompare: boolean;
  getSelectionAgeSeconds: () => number | undefined;
};

const CompareContext = createContext<CompareContextType | null>(null);

export const useCompare = (): CompareContextType => {
  const ctx = useContext(CompareContext);
  if (!ctx) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return ctx;
};

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const [datasets, setDatasets] = useState<CompareDataset[]>([]);

  const addDataset = useCallback((dataset: CompareDataset) => {
    setDatasets((current) => {
      if (current.some((d) => isSameDatasetRef(d, dataset))) return current;
      if (current.length >= MAX_COMPARE_DATASETS) return current;
      return [...current, dataset];
    });
  }, []);

  const removeDataset = useCallback((ref: DatasetRef) => {
    setDatasets((current) => current.filter((d) => !isSameDatasetRef(d, ref)));
  }, []);

  const clearDatasets = useCallback(() => setDatasets([]), []);

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) clearDatasets();
  }, [isAuthenticated, clearDatasets]);

  const toggleDataset = useCallback((dataset: CompareDataset) => {
    setDatasets((current) => {
      if (current.some((d) => isSameDatasetRef(d, dataset))) {
        return current.filter((d) => !isSameDatasetRef(d, dataset));
      }
      if (current.length >= MAX_COMPARE_DATASETS) return current;
      return [...current, dataset];
    });
  }, []);

  const firstSelectedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (datasets.length === 0) {
      firstSelectedAtRef.current = null;
    } else if (firstSelectedAtRef.current === null) {
      firstSelectedAtRef.current = Date.now();
    }
  }, [datasets.length]);

  const getSelectionAgeSeconds = useCallback(() => {
    if (firstSelectedAtRef.current === null) return undefined;
    return Math.round((Date.now() - firstSelectedAtRef.current) / 1000);
  }, []);

  const value = useMemo<CompareContextType>(
    () => ({
      datasets,
      addDataset,
      removeDataset,
      clearDatasets,
      toggleDataset,
      isSelected: (ref: DatasetRef) => datasets.some((d) => isSameDatasetRef(d, ref)),
      isFull: datasets.length >= MAX_COMPARE_DATASETS,
      canCompare: datasets.length >= 2,
      getSelectionAgeSeconds,
    }),
    [datasets, addDataset, removeDataset, clearDatasets, toggleDataset, getSelectionAgeSeconds]
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};
